import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ZoomIn, Store } from "lucide-react";
import { formatPrice, productImage } from "../lib/api";
import { useDiscount, computeDiscountedPrice } from "../lib/discount";
import { useVendor } from "../lib/vendors";
import { useLanguage } from "../lib/language";
import { conditionLabel } from "../lib/labels";
import { CATEGORY_LABELS_EN } from "../lib/categories";
import Lightbox from "./Lightbox";
import { SmartImg } from "./SmartImg";

// Known Spanish stock labels emitted by the backend → English equivalents.
// The backend currently returns Spanish strings (e.g. "ÚLTIMO DISPONIBLE"); until
// we expose stock_label_en we translate on the client so US/EN never leaks Spanish.
const STOCK_LABEL_EN = {
    "ÚLTIMO DISPONIBLE": "LAST ONE",
    "ULTIMO DISPONIBLE": "LAST ONE",
    "POCAS UNIDADES": "LOW STOCK",
    "POCAS": "LOW STOCK",
    "Agotado": "Sold out",
    "AGOTADO": "SOLD OUT",
    "Reservado": "Reserved",
    "RESERVADO": "RESERVED",
    "Nuevo ingreso": "New arrival",
    "NUEVO INGRESO": "NEW ARRIVAL",
};

export default function ProductCard({ p, eager = false }) {
    const available = typeof p.available === "number" ? p.available : (p.cantidad_disponible || 0);
    const isOut = p.estado === "Vendido" || p.cantidad_disponible === 0;
    const isReserved = available === 0 && (p.reservado || 0) > 0;
    const { discount } = useDiscount();
    const { final, original, percent } = computeDiscountedPrice(p, discount);
    const hasDiscount = percent > 0 && final < original;
    const { t, lang, isEnglish } = useLanguage();

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const navigate = useNavigate();
    const vendor = useVendor(p.vendedor_id);
    // Display fields: prefer *_en fields if available on EN site, else fall back to
    // the client-side category dictionary, else raw Spanish key.
    const displayName = isEnglish && p.nombre_en ? p.nombre_en : p.nombre;
    const displayCategoria = isEnglish
        ? (p.categoria_en || CATEGORY_LABELS_EN[p.categoria] || p.categoria)
        : p.categoria;
    const displayStockLabel = isEnglish && p.stock_label
        ? (STOCK_LABEL_EN[p.stock_label] || STOCK_LABEL_EN[String(p.stock_label).toUpperCase()] || p.stock_label)
        : p.stock_label;
    // Collect all images available on the product (main + extras) for the lightbox.
    const mainImage = productImage(p);
    const galleryImages = React.useMemo(() => {
        const out = [];
        const full = p.foto_full || mainImage;
        if (full) out.push(full);
        (Array.isArray(p.images) ? p.images : []).forEach((s) => { if (s && s !== full) out.push(s); });
        return out;
    }, [mainImage, p.foto_full, p.images]);

    const openLightbox = (e) => {
        // Prevent the surrounding <Link> from navigating.
        e.preventDefault();
        e.stopPropagation();
        setLightboxOpen(true);
    };
    const closeLightbox = () => setLightboxOpen(false);

    return (
        <>
            <Link to={`/producto/${p.sku}`} className="block group" data-testid={`product-card-${p.sku}`}>
                <div className="tb-card overflow-hidden">
                    <div className="relative aspect-[4/5] overflow-hidden bg-[var(--tb-surface-2)]">
                        {mainImage ? (
                            <SmartImg src={mainImage} alt={displayName} eager={eager} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-[var(--tb-muted)]">{t("Sin imagen", "No image")}</div>
                        )}

                        {/* Zoom button — opens the lightbox without navigating to the detail page. */}
                        {mainImage && (
                            <button
                                type="button"
                                onClick={openLightbox}
                                className="absolute bottom-2 right-2 bg-black/70 hover:bg-[var(--tb-gold)] hover:text-black text-white rounded-full w-9 h-9 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100 shadow-lg"
                                aria-label={t(`Ver ${displayName} en grande`, `View ${displayName} full size`)}
                                data-testid={`product-card-zoom-${p.sku}`}
                                title={t("Ver imagen en grande", "View full-size image")}>
                                <ZoomIn className="w-4 h-4" />
                            </button>
                        )}

                        {/* Left-side stack of badges */}
                        <div className="absolute top-1.5 left-1.5 md:top-3 md:left-3 flex flex-col gap-1 max-w-[60%]">
                            <span className={`tb-badge ${p.condicion === "Nuevo" ? "tb-badge-gold" : "tb-badge-warm"} bg-black/70 !text-[9px] md:!text-xs !px-1.5 md:!px-2 !py-0.5`}>
                                {conditionLabel(p.condicion, lang)}
                            </span>
                            {isOut && <span className="tb-badge tb-badge-warning bg-black/70 !text-[9px] md:!text-xs !px-1.5 md:!px-2 !py-0.5">{t("Agotado", "Sold out")}</span>}
                            {!isOut && p.stock_label && <span className="tb-badge tb-badge-warning bg-black/70 !text-[9px] md:!text-xs !px-1.5 md:!px-2 !py-0.5">{displayStockLabel}</span>}
                            {hasDiscount && <span className="tb-badge bg-red-500 text-white !text-[9px] md:!text-xs !px-1.5 md:!px-2 !py-0.5">-{percent}%</span>}
                            {p.sold_count > 0 && (
                                <span className="inline-flex items-center gap-0.5 bg-black/70 text-emerald-300 border border-emerald-400/40 rounded px-1.5 py-0.5 !text-[9px] md:!text-[10px] font-medium">
                                    🔥 {p.sold_count} {p.sold_count === 1 ? t("vendido", "sold") : t("vendidos", "sold")}
                                </span>
                            )}
                        </div>
                        {/* Right-side SKU */}
                        <div className="absolute top-1.5 right-1.5 md:top-3 md:right-3">
                            <span className="text-[9px] md:text-xs text-white bg-black/70 px-1.5 md:px-2 py-0.5 font-mono">{p.sku}</span>
                        </div>
                    </div>
                    <div className="p-3 md:p-5">
                        <div className="tb-overline text-[var(--tb-muted)] mb-0.5 md:mb-1 !text-[10px] md:!text-xs truncate">{displayCategoria}</div>
                        <h3 className="font-display text-sm md:text-lg leading-tight mb-2 group-hover:text-[var(--tb-gold)] transition-colors line-clamp-2 min-h-[2.5em]">{displayName}</h3>
                        {vendor && (
                            <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/v/${vendor.id}`); }}
                                className="inline-flex items-center gap-1 text-[10px] md:text-xs text-[var(--tb-text-2)] hover:text-[var(--tb-gold)] mb-1.5 md:mb-2 max-w-full truncate"
                                aria-label={t(`Ver perfil de ${vendor.display_name}`, `View ${vendor.display_name}'s profile`)}
                                data-testid={`product-card-vendor-${p.sku}`}
                                title={t(`Vendido por ${vendor.display_name}`, `Sold by ${vendor.display_name}`)}
                            >
                                <Store className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">
                                    {t("Vendido por", "Sold by")} <span className="text-[var(--tb-gold)] underline-offset-2 hover:underline">{vendor.display_name}</span>
                                </span>
                            </button>
                        )}
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1">
                            <div className="flex items-baseline gap-1.5 flex-wrap">
                                <div className="text-base md:text-xl font-semibold text-[var(--tb-gold)]">{formatPrice(final)}</div>
                                {hasDiscount && <div className="text-[10px] md:text-xs text-[var(--tb-muted)] line-through">{formatPrice(original)}</div>}
                            </div>
                            <div className="text-[10px] md:text-xs text-[var(--tb-text-2)] whitespace-nowrap">
                                {isReserved ? t("Reservado", "Reserved") : `${available} ${t("disp.", "avail.")}`}
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
            {lightboxOpen && galleryImages.length > 0 && (
                <Lightbox images={galleryImages} startIndex={0} alt={displayName} onClose={closeLightbox} />
            )}
        </>
    );
}
